import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { Flows } from '@fortem-monorepo/web-sdk'

import { Section } from '@/pages/funds/flows/components/Section'
import { to2dpNoRounding, toDecimalPlaces } from '@/pages/funds/flows/components/formatters'
import { StreamStageProps } from '@/pages/funds/flows/components/types'

export interface GetSummaryStageProps extends StreamStageProps<Flows.GetSummaryOutput> {}

export const GetSummaryStage: React.FC<GetSummaryStageProps> = ({ output }) => {
  return (
    <div className={'space-y-4'}>
      {output.last_adjustment ? (
        <div className="flex items-center gap-2">
          <span className={'text-sm text-muted-foreground'}>Last Adjustment: </span>
          <span className={'text-sm'}>
            <span className={'font-medium'}>{output.last_adjustment?.adjustment} on </span>
            <span>{new Date(output.last_adjustment.adjustment_date).toDateString()}</span>
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className={'text-sm text-muted-foreground'}>Last Adjustment: </span>
          <span className={'text-sm'}>No adjustments found</span>
        </div>
      )}

      <Section title="Summary" defaultOpen={true}>
        <Widget exposed>
          <WidgetContent>
            <CustomAgGrid
              rowData={[output]}
              columnDefs={colDefs()}
              statusBar={{ statusPanels: [] }}
            />
          </WidgetContent>
        </Widget>
      </Section>
      <Section title="Breakdown">
        <Widget exposed>
          <WidgetHeaderWithQuery
            title={`Flows since ${output.last_adjustment ? output.last_adjustment.adjustment_date : toISO8601(new Date())}`}
          />
          <WidgetContent>
            <CustomAgGrid rowData={output.breakdown} columnDefs={breakdownColDefs()} />
          </WidgetContent>
        </Widget>
      </Section>
    </div>
  )
}

const colDefs = () => {
  return createColDefs<Flows.GetSummaryOutput>([
    {
      field: 'fund_name',
      headerName: 'Fund',
      filter: false,
    },
    {
      field: 'fund_currency_name',
      headerName: 'Base Ccy',
      filter: false,
    },
    {
      field: 'cumulative_net_flow',
      headerName: 'Net Flows',
      valueFormatter: to2dpNoRounding,
      filter: false,
    },
    {
      field: 'nav',
      headerName: 'NAV',
      valueFormatter: to2dpNoRounding,
      filter: false,
    },
    {
      field: 'nav_date',
      headerName: 'NAV Date',
      width: 120,
      filter: false,
    },
    {
      field: 'cumulative_net_flow_pct_over_nav',
      headerName: '% of NAV',
      valueFormatter: (params) => {
        if (typeof params.value == 'number') {
          return `${toDecimalPlaces(params.value * 100, 4)}%`
        }
        return params.value
      },
      filter: false,
    },
    {
      field: 'action',
      headerName: 'Action',
      filter: false,
    },
  ])
}

const breakdownColDefs = () => {
  return createColDefs<Flows.DailyFundCurrencyFlow>([
    {
      hide: true,
      field: 'flow_id',
    },
    {
      rowGroup: true,
      hide: true,
      field: 'date',
    },
    {
      field: 'currency_name',
      headerName: 'Currency',
      width: 100,
      filter: false,
    },
    {
      field: 'notional',
      valueFormatter: to2dpNoRounding,
      width: 150,
      filter: false,
    },
    {
      field: 'rate',
      headerName: 'Fx Rate',
      width: 150,
      filter: false,
    },
    {
      field: 'rate_date',
      headerName: 'Fx Date',
      width: 100,
      filter: false,
    },
    {
      field: 'notional_fx',
      valueFormatter: to2dpNoRounding,
      width: 150,
      filter: false,
    },
  ])
}
